/* eslint-env node */
// Shared, dependency-free history policy. Used by the server and browser.
// Dates here are encounter calendar dates, never creation/edit-time fallbacks.
const CLINICAL_FIELDS = new Set([
  'chief_complaint', 'psychiatric_history', 'medical_history', 'medications', 'allergies',
  'family_history', 'social_history', 'substance_use', 'trauma_history', 'psychiatric_ROS',
  'mental_status_exam', 'assessment_factors', 'diagnostic_impressions_data', 'risk_assessment',
  'treatment_plan', 'after_visit_summary', 'document_summary', 'short_summary', 'updated_assessment', 'therapyType', 'therapyMinutes',
]);
const PRIVATE_FIELDS = new Set(['text', 'transcript', 'interview_transcript', 'cleaned_text',
  'processed_text', 'textSource', 'transcriptionData', 'source', 'privateMetadata', 'metadata', 'prompt', 'prompts', 'processingError', 'error', 'comments', 'comment', 'audioUrl', 'audioPath']);
const METADATA = new Set(['id', 'title', 'type', 'typeId', 'documentTypeId', 'documentTypeName',
  'sourceType', 'status', 'documentDate', 'createdAt', 'modifiedAt', 'updatedAt', 'createdBy',
  'authorId', 'userId', 'companyId', 'patientId', 'humanReview', 'processingStatus', 'processingProgress',
  'sectionsInitialized', 'sectionsComplete', 'steps', 'sectionContent', 'settings', 'sharedWith',
  'sharedAccess', 'reviewedSections', 'approvedAt', 'approvedBy', 'exportedAt', 'isArchived', 'isDeleted',
  'isHistory', 'isNew', 'humanAdded', 'hasPrivateSource', 'historyEvidence', 'historyProvenance',
  'reviewedEvidenceRevision', 'approvalRevoked', 'modifiedAfterApproval', '__proto__', 'prototype', 'constructor']);

function timestampMillis(value) {
  if (value == null || value === '') return null;
  let ms;
  if (value instanceof Date) ms = value.getTime();
  else if (typeof value?.toMillis === 'function') ms = value.toMillis();
  else if (typeof value?.toDate === 'function') ms = value.toDate().getTime();
  else if (Number.isFinite(value?.seconds ?? value?._seconds)) ms = (value.seconds ?? value._seconds) * 1000;
  else if (typeof value === 'number') ms = value;
  else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function encounterDate(value) {
  let year, month, day;
  if (typeof value === 'string') {
    let match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(value);
    if (match) {
      if (value.includes('T') && !Number.isFinite(Date.parse(value))) return null;
      [, year, month, day] = match.map(Number);
    }
    else {
      match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
      if (!match) return null;
      [, month, day, year] = match.map(Number);
    }
  } else {
    const ms = timestampMillis(value);
    if (ms === null) return null;
    const date = new Date(ms);
    year = date.getUTCFullYear(); month = date.getUTCMonth() + 1; day = date.getUTCDate();
  }
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
const encounterMillis = doc => {
  const date = encounterDate(doc?.documentDate);
  return date ? Date.parse(`${date}T00:00:00Z`) : null;
};
function compareEncounters(a, b) {
  const ad = encounterDate(a?.documentDate), bd = encounterDate(b?.documentDate);
  if (ad !== bd) return ad == null ? 1 : bd == null ? -1 : bd.localeCompare(ad);
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}
function formatEncounterDate(value, options = {}) {
  const date = encounterDate(value);
  return date ? new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', ...options, timeZone: 'UTC',
  }) : 'Date unknown';
}

function stripHistoryHtml(value) {
  return String(value).replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}
function clinicalValue(value, depth = 0) {
  if (depth > 30 || value == null) return null;
  if (typeof value === 'string') return stripHistoryHtml(value) || null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'object' || value.isDeleted === true) return null;
  if (Array.isArray(value)) return value.map(v => clinicalValue(v, depth + 1)).filter(v => v !== null);
  if (value instanceof Date || typeof value.seconds === 'number' || typeof value._seconds === 'number') return null;
  return Object.fromEntries(Object.keys(value).sort().filter(key => !METADATA.has(key) && (key === 'text' || !PRIVATE_FIELDS.has(key)))
    .map(key => [key, clinicalValue(value[key], depth + 1)])
    .filter(([, val]) => val !== null && (typeof val !== 'object' || Object.keys(val).length)));
}
function isSection(value) {
  if (!value || typeof value !== 'object') return false;
  if (Object.prototype.hasOwnProperty.call(value, 'summary')) return true;
  return Object.values(value).some(row => row && typeof row === 'object' &&
    ['summary', 'name', 'originalName'].some(key => Object.prototype.hasOwnProperty.call(row, key)));
}
function clinicalSections(doc, additionalFields = []) {
  const allowed = new Set([...CLINICAL_FIELDS, ...additionalFields]);
  const result = {};
  for (const key of Object.keys(doc || {}).sort()) {
    if (PRIVATE_FIELDS.has(key) || METADATA.has(key) || key.startsWith('_') || key.startsWith('qa')) continue;
    // Derived longitudinal rollups are not independent source evidence.
    if (key === 'history_summaries' || /_history_summary$/.test(key)) continue;
    if (!allowed.has(key) && !isSection(doc[key])) continue;
    const value = clinicalValue(doc[key]);
    if (value !== null && (typeof value !== 'object' || Object.keys(value).length)) result[key] = value;
  }
  return result;
}
function clinicalText(value) {
  if (value == null) return '';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return value.map(clinicalText).filter(Boolean).join('\n');
  return Object.entries(value).map(([key, val]) => {
    const text = clinicalText(val);
    return typeof val === 'object' || key === 'summary' || /^\d+$/.test(key) ? text : `${key.replace(/_/g, ' ')}: ${text}`;
  }).filter(Boolean).join('\n');
}
function reviewState(doc) {
  if (doc?.approvalRevoked === true || doc?.modifiedAfterApproval === true) return 'unreviewed';
  const approved = timestampMillis(doc?.approvedAt), exported = timestampMillis(doc?.exportedAt);
  const modified = timestampMillis(doc?.modifiedAt);
  const reviewedAt = Math.max(approved ?? -Infinity, exported ?? -Infinity);
  // A newer content revision is checked by the server; legacy records also need
  // a review/export timestamp at least as recent as their last edit.
  if (!doc?.reviewedEvidenceRevision && modified !== null && modified > reviewedAt) return 'unreviewed';
  if (doc?.status === 'exported' && exported !== null) return 'exported';
  if (['approved', 'exported'].includes(doc?.status) && approved !== null) return 'approved';
  return 'unreviewed';
}
function historyExclusion(doc, { reviewedOnly = false, excludeDocumentId, beforeDate } = {}) {
  if (!doc || doc.isArchived === true || doc.isDeleted === true || doc.deletedAt) return 'archived-or-deleted';
  if (excludeDocumentId && doc.id === excludeDocumentId) return 'current-document';
  if (!['completed', 'approved', 'exported'].includes(doc.status)) return 'unfinished';
  if (doc.type === 'historySummary' || ['summary', 'export'].includes(String(doc.sourceType).toLowerCase())) return 'derived';
  if (reviewedOnly && (doc.historyEvidence?.reviewState || reviewState(doc)) === 'unreviewed') return 'unreviewed';
  const date = encounterDate(doc.documentDate);
  if (beforeDate && (!date || date > beforeDate)) return 'outside-encounter-range';
  if (!Object.keys(clinicalSections(doc)).length) return 'empty';
  return null;
}
function selectHistoryDocuments(documents, options = {}) {
  return (documents || []).filter(doc => !historyExclusion(doc, options)).slice().sort(compareEncounters);
}

function projectHistoryDocument(doc) {
  return { id: doc.id || null, type: doc.type || '', documentDate: encounterDate(doc.documentDate),
    historyEvidence: doc.historyEvidence || { reviewState: reviewState(doc) }, ...clinicalSections(doc) };
}

module.exports = { projectHistoryDocument, CLINICAL_FIELDS, PRIVATE_FIELDS, METADATA, timestampMillis, encounterDate, encounterMillis,
  compareEncounters, formatEncounterDate, stripHistoryHtml, clinicalValue, clinicalSections, clinicalText,
  reviewState, historyExclusion, selectHistoryDocuments };
